import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de proveedores le pide a la API (#56), con la forma del
// `datos.ts` de sucursales: clave de caché con el taller adentro, una consulta
// por lectura y una función por escritura que devuelve el `ProblemDetails` o nada.
//
// Lo propio de proveedores: el alta también la usa la ventana de crear al vuelo
// (`VentanaProveedor`), que los gastos de la fase 3 abren sin salir de lo que
// están capturando. Por eso `crear` devuelve el proveedor creado.

export type Proveedor = components['schemas']['SupplierListItem'];
export type FichaProveedor = components['schemas']['SupplierDetail'];
export type ProveedorExistente = components['schemas']['ExistingSupplier'];

/** El 409 del alta y la edición: si chocó el nombre o el documento, trae al que ya los tiene. */
export type ProblemaProveedor = ProblemaApi & { existingSupplier?: ProveedorExistente | null };

/** Lo que devuelve el alta al vuelo: lo justo para dejarlo elegido en un combo. */
export interface ProveedorCreado {
  id: string;
  code: string;
  name: string;
}

/// Lo que edita el formulario. Los nombres son los del contrato: los errores por
/// campo de la API llegan con esas claves. `code` vacío al crear deja que lo
/// proponga el correlativo del taller.
export interface ValoresProveedor {
  code: string;
  name: string;
  docTypeId: string;
  docIdentifier: string;
  reference: string;
}

export interface FiltrosProveedores {
  buscar: string;
  estado: 'todos' | 'activos' | 'inactivos';
  cursor: string | null;
}

/// El taller de la pestaña (#205): va en toda clave de caché (regla 5 de multitenancy,
/// en el navegador). Ver `sucursales/datos.ts`.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['proveedores', taller] as const,
  lista: (taller: string, filtros: FiltrosProveedores) => ['proveedores', taller, 'lista', filtros] as const,
  ficha: (taller: string, id: string) => ['proveedores', taller, 'ficha', id] as const,
  // Bajo `proveedores`: guardar, activar o desactivar invalidan esa rama, y el
  // historial se relee solo.
  historial: (taller: string, id: string) => ['proveedores', taller, 'historial', id] as const,
  // Fuera de `proveedores`: guardar invalida esa rama, y volver a pedir el código
  // justo antes de salir del alta sería una consulta para nadie.
  siguienteCodigo: (taller: string) => ['siguienteCodigoProveedor', taller] as const,
  tiposDocumento: (taller: string) => ['tiposDocumento', taller, 'todos'] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** Espacios afuera, y vacío pasa a `null`: la API guarda «sin referencia», no un texto vacío. */
const opcional = (texto: string) => texto.trim() || null;

/** El documento va en pareja o no va: sin tipo, el número no se manda. */
function documento(v: ValoresProveedor) {
  return v.docTypeId ? { docTypeId: v.docTypeId, docIdentifier: opcional(v.docIdentifier) } : {};
}

export function useProveedores(filtros: FiltrosProveedores) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/suppliers', {
        params: {
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.estado !== 'todos' && { isActive: filtros.estado === 'activos' }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista de proveedores');
      return data;
    },
    // Mientras llega la página siguiente se sigue viendo la anterior: sin esto la
    // tabla parpadea a «Cargando…» en cada tecla del buscador.
    placeholderData: keepPreviousData,
  });
}

export function useFichaProveedor(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/suppliers/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: el proveedor no existe para este taller.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el proveedor');
      return data;
    },
    // Siempre fresca al abrir la edición: con la versión vieja en caché, guardar
    // chocaría contra la edición de otro que ya estaba en la base.
    staleTime: 0,
  });
}

/**
 * El historial del proveedor (#188): quién lo registró y quién le cambió qué, lo
 * más nuevo primero. Pide lo mismo que la ficha, `supplier.edit`.
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialProveedor(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/suppliers/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial del proveedor');
      return data;
    },
  });
}

/**
 * Los tipos de documento del taller, sin filtrar por clase: un proveedor puede
 * ser una empresa o una persona que trabaja por su cuenta.
 */
export function useTiposDocumento() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.tiposDocumento(taller),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/document-types');
      if (error || !data) throw new Error('No se pudieron leer los tipos de documento');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * El código que tendría el próximo proveedor, para mostrarlo ya puesto en el
 * alta. No reserva nada: el alta lo manda vacío si nadie lo tocó. Sin caché: uno
 * guardado de la visita anterior ya lo habría usado otro proveedor.
 */
export function useSiguienteCodigo(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.siguienteCodigo(taller),
    enabled: habilitado,
    queryFn: pedirSiguienteCodigo,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Otro código propuesto, o vacío si no se pudo saber: el alta sigue sin él. */
export async function pedirSiguienteCodigo(): Promise<string> {
  try {
    const { data } = await api.GET('/api/suppliers/next-code');
    return data?.code ?? '';
  } catch {
    return '';
  }
}

/** Las escrituras. Cada una refresca los proveedores del taller al terminar bien. */
export function useCambiosProveedor() {
  const taller = useTaller();
  const cache = useQueryClient();

  const refrescar = () => cache.invalidateQueries({ queryKey: claves.todo(taller) });

  /**
   * El alta, de la página y de la ventana al vuelo. `codigoPropuesto` es el que se
   * mostró ya puesto: si sigue igual, se manda vacío y lo asigna el servidor, para
   * que dos altas simultáneas no choquen por el mismo número.
   */
  async function crear(
    v: ValoresProveedor,
    codigoPropuesto = '',
  ): Promise<{ creado?: ProveedorCreado; problema?: ProblemaProveedor }> {
    const codigo = v.code.trim();
    const { data, error } = await api.POST('/api/suppliers', {
      body: {
        name: v.name.trim(),
        code: codigo && codigo !== codigoPropuesto ? codigo : null,
        ...documento(v),
        reference: opcional(v.reference),
      },
    });
    if (error || !data) return { problema: error as ProblemaProveedor };
    await refrescar();
    return { creado: { id: String(data.id), code: data.code, name: v.name.trim() } };
  }

  async function editar(
    id: string,
    versionActual: number,
    activo: boolean,
    v: ValoresProveedor,
  ): Promise<ProblemaProveedor | undefined> {
    const { error } = await api.PUT('/api/suppliers/{id}', {
      params: { path: { id } },
      body: {
        version: versionActual,
        code: v.code.trim(),
        name: v.name.trim(),
        // La edición manda la ficha entera: el estado se conserva como estaba.
        isActive: activo,
        ...documento(v),
        reference: opcional(v.reference),
      },
    });
    if (error) return error as ProblemaProveedor;
    await refrescar();
    return undefined;
  }

  async function cambiarEstado(id: string, versionActual: number, activo: boolean): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/suppliers/{id}/status', {
      params: { path: { id } },
      body: { version: versionActual, isActive: activo },
    });
    if (error) return error as ProblemaApi;
    await refrescar();
    return undefined;
  }

  /** Baja lógica: los gastos que lo usan se conservan. */
  async function eliminar(id: string): Promise<ProblemaApi | undefined> {
    const { error } = await api.DELETE('/api/suppliers/{id}', { params: { path: { id } } });
    // Con el cuerpo vacío, `error` llega como `""`: falso, pero es un error.
    if (error !== undefined) return (typeof error === 'object' && error ? error : {}) as ProblemaApi;
    // Todo menos la ficha que se acaba de eliminar y su historial: pedirlos de
    // nuevo daría un 404 justo mientras la pantalla abierta sale al listado.
    const eliminadas = [claves.ficha(taller, id), claves.historial(taller, id)];
    await cache.invalidateQueries({
      queryKey: claves.todo(taller),
      predicate: (consulta) =>
        !eliminadas.some((clave) => clave.every((parte, i) => consulta.queryKey[i] === parte)),
    });
    return undefined;
  }

  return { crear, editar, cambiarEstado, eliminar };
}
